import * as React from "react";
import { Link } from "react-router-dom";
import { FiActivity, FiBarChart2, FiBriefcase, FiTarget } from "react-icons/fi";
import SleekButton from "@/components/ui/NavButtons";
import { PlatformTour } from "./PlatformTour";

export const DashboardPreview: React.FC = () => {
  const previewCards = [
    {
      label: "Portfolio Value",
      desc: "Live aggregate across all properties",
      icon: <FiBriefcase className="h-8 w-8" aria-hidden />,
    },
    {
      label: "Monthly Growth",
      desc: "Real-time performance tracking",
      icon: <FiBarChart2 className="h-8 w-8" aria-hidden />,
    },
    {
      label: "Deal Readiness",
      desc: "AI-scored investment readiness",
      icon: <FiTarget className="h-8 w-8" aria-hidden />,
    },
    {
      label: "AI Activity Feed",
      desc: "Alerts and insights as they happen",
      icon: <FiActivity className="h-8 w-8" aria-hidden />,
    },
  ];

  return (
    <section className="relative py-20 px-4 sm:px-8 dark:bg-vw-surface/40 bg-slate-50 overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] dark:bg-blue-600/10 bg-blue-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1100px] mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
            Live Platform
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">
            <span className="vw-gradient-text">See exactly</span>
            <span className="dark:text-white text-gray-900"> what you get</span>
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-600 max-w-xl mx-auto">
            This is the actual VaWeNa dashboard your investors use — portfolio value, deal readiness, market score, and AI-powered activity feed in one view.
          </p>
        </div>

        {/* Interactive platform tour — clickable tabs, like the app's Learn the app tour */}
        <PlatformTour />

        {/* Feature callouts below screenshot */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {previewCards.map(({ icon, label, desc }) => (
            <div
              key={label}
              className="group dark:bg-vw-surface bg-white border dark:border-white/[0.07] border-gray-200 rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-blue-400/70 hover:shadow-[0_12px_32px_-18px_rgba(37,99,235,0.75)] dark:hover:border-blue-500/50"
            >
              <div className="mb-3 flex justify-center">
                <div className="text-blue-500 dark:text-blue-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                  {icon}
                </div>
              </div>
              <div className="text-sm font-bold dark:text-white text-gray-900 mb-2">{label}</div>
              <div className="text-xs dark:text-slate-500 text-gray-400 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a href="https://app.vawena.ai/signup" target="_self" rel="noopener noreferrer">
            <SleekButton variant="primary" className="min-w-[200px]">
              Start Free Trial
            </SleekButton>
          </a>
          <Link to="/contact">
            <SleekButton variant="light" className="min-w-[200px]">
              Book a Demo
            </SleekButton>
          </Link>
        </div>
        <p className="mt-4 text-xs dark:text-slate-500 text-gray-400 text-center">
          14-day free trial · We'll remind you before you're charged · Cancel anytime
        </p>
      </div>
    </section>
  );
};
